import type React from 'react';
import { Table, Column } from '../../../components/ui/Table';
import { Machine } from './presenter';
import { CalendarDays, CalendarRange, TrendingUp } from 'lucide-react';

export const DashboardView: React.FC<{
  machines: Machine[];
  loading: boolean;
  error: string | null;
  summaries: { day: any; week: any; month: any };
  productCounts?: { active: number; inactive: number };
  onMachineClick?: (m: Machine) => void;
  rowDetail?: (m: Machine) => React.ReactNode;
}> = ({ machines, loading, error, summaries, productCounts: _productCounts, onMachineClick: _onMachineClick, rowDetail }) => {
  const columns: Column<Machine>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (m) => (
          <span>{m.name}</span>
      ),
    },
    { key: 'location', header: 'Location' },
    {
      key: 'status',
      header: 'Status',
      render: (m) => (
        <span className={(m.status === 'online'
          ? 'inline-flex items-center rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white'
          : 'inline-flex items-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white')}>{m.status}</span>
      ),
    },
    { key: 'lastSyncAt', header: 'Last Sync' },
  ];

  const formatIDR = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SummaryCard title="Transactions (Day)" value={summaries.day.transactions} icon={<CalendarDays className="h-4 w-4 text-muted-foreground" />} />
        <SummaryCard title="Transactions (Week)" value={summaries.week.transactions} icon={<CalendarRange className="h-4 w-4 text-muted-foreground" />} />
        <SummaryCard title="Transactions (Month)" value={summaries.month.transactions} icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <SummaryCard title="Revenue (Day)" value={formatIDR(summaries.day.revenue)} icon={<CalendarDays className="h-4 w-4 text-muted-foreground" />} />
        <SummaryCard title="Revenue (Week)" value={formatIDR(summaries.week.revenue)} icon={<CalendarRange className="h-4 w-4 text-muted-foreground" />} />
        <SummaryCard title="Revenue (Month)" value={formatIDR(summaries.month.revenue)} icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />} />
      </div>

      {loading && <div>Loading...</div>}
      {error && <div className="text-destructive">{error}</div>}
      <div>
        <div className="text-lg font-semibold mb-2">Machines</div>
        <Table
          columns={columns}
          data={machines}
          page={1}
          pageSize={machines.length || 10}
          total={machines.length}
          onPageChange={() => {}}
          rowDetail={rowDetail}
          getRowClass={(m) => m.status === 'online' ? 'bg-green-50 dark:bg-green-950/30' : 'bg-red-50 dark:bg-red-950/30'}
        />
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; value: React.ReactNode; icon?: React.ReactNode }>= ({ title, value, icon }) => (
  <div className="flex items-center justify-between rounded-md border bg-card p-3 shadow-sm">
    <div>
      <div className="text-sm text-muted-foreground">{title}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
    {icon && <div className="ml-3 rounded-md bg-muted p-2">{icon}</div>}
  </div>
);

export default DashboardView;
